import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Button from 'components/helpers/Button';
import { ButtonVariant } from 'components/helpers/Button/WithButton';
import Heading, { propsFromFields } from 'components/helpers/Heading';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import { getEnumValue } from 'lib/util/enum';
import { classnames, TTailwindString } from 'tailwindcss-classnames';

type stats = Sitecore.Override.ItemEx &
  Components.General.Stats.Fields.StatItem & {
    id: string;
  };
export type StatsProps = Sitecore.Override.ComponentBase &
  Components.General.Stats.Fields.Stats & {
    fields: {
      statItems: stats[];
    };
  };

export type DecorationOptions = 'decoration-plum' | 'decoration-white' | 'decoration-night-light';

export const DecorationOptionsDefault: DecorationOptions = 'decoration-plum';

const decorations: Record<DecorationOptions, TTailwindString> = {
  'decoration-plum': classnames('bg-plum', 'text-white'),
  'decoration-night-light': classnames('bg-blue-night-tint', 'text-blue-night'),
  'decoration-white': classnames('bg-white', 'text-blue-night'),
};

/**
 * Return relevant classes for the selected theme, or return null
 * if no additional theme classes need to be applied
 * @param {DecorationOptions | 'inherit' | undefined} decoration The theme option to get classes for
 * @returns {TTailwindString | null} The relevant tailwind-classnames TArg
 */
const getDecorationClasses = (
  decoration: DecorationOptions | undefined
): TTailwindString | null => {
  return typeof decoration === 'undefined' ? null : decorations[decoration];
};

const Stats = ({ fields }: StatsProps): JSX.Element => {
  const isExperienceEditor = useExperienceEditor();
  // Fail out if we don't have any fields
  if (!fields) {
    return <></>;
  }
  const headingProps = propsFromFields(fields);
  const cta2TypeField = fields.cta2Type.value;
  const textCenter = `text-center`;
  const cta2Type: ButtonVariant =
    cta2TypeField === 'Default' || cta2TypeField.trim().length === 0
      ? 'secondary'
      : (cta2TypeField.toLowerCase() as ButtonVariant);

  const flexAlignment = classnames({
    'justify-start': headingProps.headingAlignment === 'left',
    'justify-center': headingProps.headingAlignment === 'center',
    'justify-end': headingProps.headingAlignment === 'right',
  });
  const renderImage = fields?.statsImage?.value?.src || isExperienceEditor;
  const scImagePlacehoder =
    !renderImage && isExperienceEditor ? 'stats-image ee-placeholder ' : 'stats-image ';
  const statsBackground: DecorationOptions | undefined =
    getEnumValue<DecorationOptions>(fields?.statsBackgroundDecoration) || undefined;
  let statsContainerPadding = classnames('md:px-20');
  let statsColumnWidth = classnames('lg:w-1/3');
  if (fields.statItems.length > 3) {
    statsContainerPadding = classnames('md:px-10');
    statsColumnWidth = classnames('lg:w-1/4');
  }
  let statsClasses = 'stat-items';
  if (statsBackground) {
    statsClasses += ` bg-decoration ${classnames(
      statsContainerPadding,
      'md:pt-10',
      'pt-20',
      'md:pb-8',
      'pb-16',
      'px-5',
      'mt-10',
      'md:mb-5',
      'mb-12',
      'relative',
      getDecorationClasses(statsBackground)
    )}`;
  }

  const statsImageClasses =
    typeof fields.statsImage.value?.class === 'string' ? fields.statsImage.value?.class : '';

  // Return Element
  return (
    <div className="stats theme-navy-blue text-white" data-component="authorable/general/stats">
      <Heading
        {...headingProps}
        className={classnames('mb-4')}
        descriptionClassName={classnames('lg:px-6', 'md:max-w-3xl', 'md:mx-auto')}
      />
      {isExperienceEditor || fields.cta1.value.href || fields.cta2.value.href ? (
        <div
          className={classnames(
            'flex',
            'flex-col',
            'md:flex-row',
            'items-baseline',
            'items-stretch',
            textCenter,
            flexAlignment
          )}
        >
          <Button linkField={fields.cta1} className={classnames('mt-6', 'md:mr-4')} />
          <Button linkField={fields.cta2} variant={cta2Type} className={classnames('mt-6')} />
        </div>
      ) : (
        <></>
      )}
      <div className={statsClasses}>
        <div
          className={classnames(
            'flex',
            'flex-wrap',
            'grid-cols-4',
            'sm:justify-start',
            'justify-center',
            'md:pb-0',
            'pb-2'
          )}
        >
          {fields.statItems.map((statItem: stats) => (
            <div
              key={statItem.id}
              className={classnames(
                statsColumnWidth,
                'pr-4',
                'pl-4',
                'sm:w-1/2',
                'pr-4',
                'pl-4',
                'w-full',
                textCenter,
                'mb-8',
                'md:mb-5'
              )}
            >
              <div>
                <h3
                  className={classnames(
                    'font-bold',
                    'leading-9',
                    'sm:leading-11',
                    'mb-1',
                    textCenter
                  )}
                >
                  <Text field={statItem.fields.statHeading} />
                </h3>
                <RichTextA11yWrapper field={statItem.fields.statBody} className="stat-details" />
              </div>
            </div>
          ))}
        </div>
      </div>
      {renderImage ? (
        <div
          className={
            scImagePlacehoder + classnames('relative', 'md:-mr-20', 'md:-ml-20', 'md:mt-5')
          }
        >
          <ImageWrapper field={fields.statsImage} className={statsImageClasses} />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default Stats;
